/**
 * The composer's text box: a ProseMirror editor holding lines of text,
 * inline code, code blocks and lists, which gives the page the same Markdown
 * text a `<textarea>` would have held. `codeText.ts` reads that text back in.
 *
 * Typing code:
 * - A backtick then a space opens an inline code box. A backtick, or → at
 *   the end of the line, leaves it; `text` typed whole is code too.
 * - A line that is only a fence, ``` or ```python, becomes a code block on
 *   Shift+Enter, or on Enter, which would otherwise send it. Shift+Enter is a
 *   new line inside it. ``` on its last line, ↓ or → at its end, or a third
 *   Shift+Enter after two empty lines leaves it; ↑ at the start of one that
 *   opens the draft makes a line above it. Backspace at its start turns it
 *   back into lines. Its code is coloured by `highlight.ts`.
 * - Backspace straight after any of these puts back what was typed.
 *
 * Typing lists:
 * - "- " or "* " at the start of a line starts a bulleted list, and "1. " a
 *   numbered one, counting from the number typed.
 * - Shift+Enter, or Enter on a touch keyboard, is a new item; on an empty one
 *   it leaves the list. Tab and Shift+Tab nest and un-nest an item, and
 *   Backspace at an item's start takes it out of its list.
 *
 * An empty inline box holds a zero-width space, so there is something to
 * draw the box around. It goes when the first character is typed, or when
 * the cursor leaves the box, and it is never in the text.
 */

import { baseKeymap, chainCommands, newlineInCode, splitBlock } from 'prosemirror-commands';
import { history, redo, undo } from 'prosemirror-history';
import { InputRule, inputRules, undoInputRule, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { Schema, Slice, type Node } from 'prosemirror-model';
import { EditorState, Plugin, PluginKey, Selection, TextSelection, type Command, type Transaction } from 'prosemirror-state';
import { liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';
import 'prosemirror-view/style/prosemirror.css';
import { fenced, inlineCode, parseCodeText, type List, type Span } from '../codeText';
import { highlight } from '../highlight';
import { inlineCode as inlineCodeStyle } from '../Markdown';
import { pastedFiles } from './images';

export const ZWSP = '\u200b';

/** Like a reply's code block (`CodeBlock` in Markdown.tsx), without its bar. */
const PRE_STYLE = [
  'position: relative',
  'margin: 2px 0',
  'padding: 10px 14px',
  'border-radius: var(--radius-app-card)',
  'background: var(--surface-code)',
  'color: var(--slate-200)',
  'font-family: var(--font-mono)',
  'font-size: 13px',
  'line-height: 1.6',
  'tab-size: 4',
  'white-space: pre-wrap',
  'overflow-wrap: anywhere'
].join('; ');

/** Lines wrap, so a block that names its language starts them below the
 *  label in its corner (`.composer-code::after`) rather than under it. */
const LABELLED_STYLE = 'padding-top: 24px';

/** Like a reply's lists (`blocks` in Markdown.tsx). */
const LIST_STYLE = 'margin: 0; padding-left: 22px';
const ITEM_STYLE = 'padding-left: 2px';

export const schema = new Schema({
  nodes: {
    doc: { content: 'block+' },
    paragraph: {
      group: 'block',
      content: 'text*',
      marks: 'code',
      parseDOM: [{ tag: 'p' }],
      toDOM: () => ['p', { style: 'margin: 0' }, 0]
    },
    code_block: {
      group: 'block',
      content: 'text*',
      marks: '',
      code: true,
      defining: true,
      attrs: { lang: { default: '' } },
      parseDOM: [{ tag: 'pre', preserveWhitespace: 'full', getAttrs: (dom) => ({ lang: (dom as HTMLElement).dataset.lang ?? '' }) }],
      toDOM: (node) => [
        'pre',
        { 'data-lang': node.attrs.lang as string, spellcheck: 'false', class: 'composer-code', style: node.attrs.lang ? `${PRE_STYLE}; ${LABELLED_STYLE}` : PRE_STYLE },
        ['code', 0]
      ]
    },
    bullet_list: {
      group: 'block',
      content: 'list_item+',
      parseDOM: [{ tag: 'ul' }],
      toDOM: () => ['ul', { style: LIST_STYLE }, 0]
    },
    ordered_list: {
      group: 'block',
      content: 'list_item+',
      attrs: { order: { default: 1 } },
      parseDOM: [{ tag: 'ol', getAttrs: (dom) => ({ order: Number((dom as HTMLElement).getAttribute('start') ?? 1) || 1 }) }],
      toDOM: (node) => ['ol', { start: String(node.attrs.order), style: LIST_STYLE }, 0]
    },
    // An item is one line, and the lists nested under it: no code block.
    list_item: {
      content: 'paragraph (bullet_list | ordered_list)*',
      defining: true,
      parseDOM: [{ tag: 'li' }],
      toDOM: () => ['li', { style: ITEM_STYLE }, 0]
    },
    text: {}
  },
  marks: {
    code: {
      code: true,
      parseDOM: [{ tag: 'code' }],
      toDOM: () => {
        const el = document.createElement('code');
        Object.assign(el.style, inlineCodeStyle);
        el.spellcheck = false;
        return { dom: el, contentDOM: el };
      }
    }
  }
});

const { paragraph, code_block: codeBlock, bullet_list: bulletList, ordered_list: orderedList, list_item: listItem } = schema.nodes;
const code = schema.marks.code;
const isList = (node: Node): boolean => node.type === bulletList || node.type === orderedList;

// ── text in and out ─────────────────────────────────────────────────────────

/** A line's text, with its inline code. */
function lineOf(block: Node): string {
  let line = '';
  block.forEach((node) => {
    const text = (node.text ?? '').replaceAll(ZWSP, '');
    line += code.isInSet(node.marks) ? inlineCode(text) : text;
  });
  return line;
}

/** A list's lines: `- ` or `1. ` before each item, and the lists nested
 *  under an item indented to its text. */
function listLines(list: Node, indent: string, lines: string[]): void {
  let n = list.type === orderedList ? (list.attrs.order as number) : 0;
  list.forEach((item) => {
    const marker = list.type === orderedList ? `${n++}. ` : '- ';
    item.forEach((child, _, i) => {
      if (i === 0) lines.push(indent + marker + lineOf(child));
      else listLines(child, indent + ' '.repeat(marker.length), lines);
    });
  });
}

/** The draft as the text the page sends. */
export function toText(doc: Node): string {
  const lines: string[] = [];
  doc.forEach((block) => {
    if (block.type === codeBlock) lines.push(fenced(block.attrs.lang as string, block.textContent.replaceAll(ZWSP, '')));
    else if (isList(block)) listLines(block, '', lines);
    else lines.push(lineOf(block));
  });
  return lines.join('\n');
}

const lineNode = (spans: Span[]): Node =>
  paragraph.create(
    null,
    spans.filter((s) => s.text).map((s) => schema.text(s.text, s.code ? [code.create()] : null))
  );

const listNode = (list: List): Node => {
  const items = list.items.map((item) => listItem.create(null, [lineNode(item.spans), ...item.lists.map(listNode)]));
  return list.ordered ? orderedList.create({ order: list.start }, items) : bulletList.create(null, items);
};

/** Text as a draft: `codeText.ts`'s lines, fences and lists. */
export function fromText(text: string): Node {
  const blocks = parseCodeText(text).map((p) =>
    p.t === 'fence'
      ? codeBlock.create({ lang: p.lang }, p.text ? schema.text(p.text) : null)
      : p.t === 'list'
        ? listNode(p.list)
        : lineNode(p.spans)
  );
  return schema.nodes.doc.create(null, blocks);
}

/** Nothing typed: one empty line. */
export const isEmpty = (doc: Node): boolean =>
  doc.childCount === 1 && doc.firstChild!.type === paragraph && doc.firstChild!.content.size === 0;

// ── typing code ─────────────────────────────────────────────────────────────

const cursorOf = (state: EditorState) => (state.selection instanceof TextSelection ? state.selection.$cursor : null);

// Each rule's handler gets the match, where it starts and the cursor. What
// was just typed is the end of the match and is not in the document yet, and
// it can be more than one character: an input method, or a fast typist,
// sends "` " at once. So each works out where its own backtick is.

/** "` " typed: its backtick becomes an empty inline box with the cursor in it. */
function openInlineCode(state: EditorState, match: RegExpMatchArray, start: number, end: number): Transaction {
  const tick = Math.min(start + match[0].length - 2, end);
  const tr = state.tr.replaceWith(tick, end, schema.text(ZWSP, [code.create()]));
  return tr.setSelection(TextSelection.create(tr.doc, tick + 1));
}

/** "`text`" typed whole: the text becomes code, and what follows is not. */
function closeInlineCode(state: EditorState, match: RegExpMatchArray, start: number, end: number): Transaction {
  const body = match[1]!;
  const open = Math.min(start + match[0].length - body.length - 2, end);
  return state.tr.replaceWith(open, end, schema.text(body, [code.create()])).setStoredMarks([]);
}

/** A backtick typed alone at the end of inline code: out of the box, and the
 *  backtick is not kept. An empty box goes. Null anywhere else. */
function leaveInlineCodeTyping(state: EditorState, _match: RegExpMatchArray, start: number, end: number): Transaction | null {
  if (start !== end) return null;
  const $end = state.doc.resolve(end);
  const marks = state.storedMarks ?? $end.marks();
  if (!code.isInSet(marks)) return null;
  if ($end.nodeAfter && code.isInSet($end.nodeAfter.marks)) return null;
  const tr = state.tr;
  if ($end.nodeBefore?.text === ZWSP) tr.delete(end - 1, end);
  return tr.setStoredMarks(code.removeFromSet(marks));
}

/** "```" typed as the last line of a code block: the line goes, and so does
 *  the cursor, to a line after the block. An empty block becomes that line.
 *  Null anywhere but the end of a block. */
function closeCodeBlock(state: EditorState, _match: RegExpMatchArray, start: number, end: number): Transaction | null {
  const $end = state.doc.resolve(end);
  if ($end.parent.type !== codeBlock || $end.parentOffset !== $end.parent.content.size) return null;
  const tr = state.tr.delete(start, end);
  const blockStart = $end.before();
  const block = tr.doc.nodeAt(blockStart)!;
  if (!block.content.size) {
    tr.replaceWith(blockStart, blockStart + block.nodeSize, paragraph.create());
    return tr.setSelection(TextSelection.create(tr.doc, blockStart + 1));
  }
  const after = blockStart + block.nodeSize;
  tr.insert(after, paragraph.create());
  return tr.setSelection(TextSelection.create(tr.doc, after + 1));
}

/** In the order they are tried. */
export const INPUT_RULES = [
  new InputRule(/`$/, leaveInlineCodeTyping),
  new InputRule(/(?:^|[^`])`([^`\u200b]+)`$/, closeInlineCode, { inCodeMark: false }),
  new InputRule(/(?:^|[^`])` $/, openInlineCode, { inCodeMark: false }),
  new InputRule(/(?:^|\n)```$/, closeCodeBlock, { inCode: 'only' }),
  wrappingInputRule(/^\s*([-*])\s$/, bulletList),
  // Joins the list before it when it goes on from that list's last number.
  wrappingInputRule(
    /^(\d{1,9})\.\s$/,
    orderedList,
    (m) => ({ order: Number(m[1]) }),
    (m, list) => list.childCount + (list.attrs.order as number) === Number(m[1])
  )
];

/** → at the end of a line that ends in inline code: out of the box, with a
 *  space to go on typing after it. An empty box just goes. */
export const leaveInlineCode: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  if (!$cursor || $cursor.parent.type !== paragraph || $cursor.parentOffset !== $cursor.parent.content.size) return false;
  const before = $cursor.nodeBefore;
  if (!before || !code.isInSet(before.marks)) return false;
  if (state.storedMarks && !code.isInSet(state.storedMarks)) return false;
  if (dispatch) {
    const tr = state.tr;
    if (before.text === ZWSP) tr.delete($cursor.pos - 1, $cursor.pos);
    else tr.insert($cursor.pos, schema.text(' '));
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** A line that is only a fence, ``` or ```python: a code block in its place. */
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\s`]*)[ \t]*$/;

export const openCodeBlock: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  // Not in a list, which holds no code block.
  if (!$cursor || $cursor.depth !== 1 || $cursor.parent.type !== paragraph || $cursor.parentOffset !== $cursor.parent.content.size) return false;
  let marked = false;
  $cursor.parent.forEach((n) => {
    if (code.isInSet(n.marks)) marked = true;
  });
  const m = marked ? null : FENCE_LINE.exec($cursor.parent.textContent);
  if (!m) return false;
  if (dispatch) {
    const from = $cursor.before();
    const tr = state.tr.replaceWith(from, $cursor.after(), codeBlock.create({ lang: m[2] ?? '' }));
    dispatch(tr.setSelection(TextSelection.create(tr.doc, from + 1)).scrollIntoView());
  }
  return true;
};

/** A new line after the code block the cursor ends, where the draft ends. */
export const leaveCodeBlockForward: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  if (!$cursor || $cursor.parent.type !== codeBlock || $cursor.parentOffset !== $cursor.parent.content.size) return false;
  if ($cursor.after() !== state.doc.content.size) return false;
  if (dispatch) {
    const at = $cursor.after();
    const tr = state.tr.insert(at, paragraph.create());
    dispatch(tr.setSelection(TextSelection.create(tr.doc, at + 1)).scrollIntoView());
  }
  return true;
};

/** A new line before the code block the cursor starts, where it opens the draft. */
export const leaveCodeBlockBackward: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  if (!$cursor || $cursor.parent.type !== codeBlock || $cursor.parentOffset !== 0 || $cursor.before() !== 0) return false;
  if (dispatch) {
    const tr = state.tr.insert(0, paragraph.create());
    dispatch(tr.setSelection(TextSelection.create(tr.doc, 1)).scrollIntoView());
  }
  return true;
};

/** A new line at the end of a code block that already ends in two empty
 *  ones: they go, and the cursor leaves the block. */
export const leaveCodeBlockAfterBlanks: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  if (!$cursor || $cursor.parent.type !== codeBlock) return false;
  const block = $cursor.parent;
  if ($cursor.parentOffset !== block.content.size || !block.textContent.endsWith('\n\n')) return false;
  if (dispatch) {
    const from = $cursor.before();
    const kept = block.textContent.slice(0, -2);
    const nodes = kept
      ? [codeBlock.create(block.attrs, schema.text(kept)), paragraph.create()]
      : [paragraph.create()];
    const tr = state.tr.replaceWith(from, $cursor.after(), nodes);
    const at = from + (kept ? nodes[0]!.nodeSize : 0) + 1;
    dispatch(tr.setSelection(TextSelection.create(tr.doc, at)).scrollIntoView());
  }
  return true;
};

/** Backspace at the start of a code block: its code, back as lines. */
export const unwrapCodeBlock: Command = (state, dispatch) => {
  const $cursor = cursorOf(state);
  if (!$cursor || $cursor.parent.type !== codeBlock || $cursor.parentOffset !== 0) return false;
  if (dispatch) {
    const from = $cursor.before();
    const lines = $cursor.parent.textContent.split('\n').map((l) => paragraph.create(null, l ? schema.text(l) : null));
    const tr = state.tr.replaceWith(from, $cursor.after(), lines);
    dispatch(tr.setSelection(TextSelection.create(tr.doc, from + 1)).scrollIntoView());
  }
  return true;
};

/** Tab in a code block indents by four spaces. */
const indentInCode: Command = (state, dispatch) => {
  if (state.selection.$from.parent.type !== codeBlock) return false;
  if (dispatch) dispatch(state.tr.insertText('    ').scrollIntoView());
  return true;
};

/** The cursor, where it is in a list item's line. */
const itemCursor = (state: EditorState) => {
  const $cursor = cursorOf(state);
  return $cursor && $cursor.depth >= 2 && $cursor.node(-1).type === listItem ? $cursor : null;
};

/** A new line on an empty item of a list that is not nested: out of the
 *  list. `splitListItem` takes a nested one out a level itself. */
const leaveEmptyItem: Command = (state, dispatch) => {
  const $cursor = itemCursor(state);
  if (!$cursor || $cursor.parent.content.size) return false;
  return liftListItem(listItem)(state, dispatch);
};

/** Backspace at the start of an item: out of its list, or up a level. */
export const liftAtItemStart: Command = (state, dispatch) => {
  const $cursor = itemCursor(state);
  if (!$cursor || $cursor.parentOffset !== 0) return false;
  return liftListItem(listItem)(state, dispatch);
};

/** Tab in a list nests the item under the one before; Shift+Tab takes it
 *  up a level. Either is taken in a list even when it cannot move, so focus
 *  stays in the box, as it does in a code block. */
export const nestItem: Command = (state, dispatch) => {
  if (!itemCursor(state)) return false;
  sinkListItem(listItem)(state, dispatch);
  return true;
};
export const unnestItem: Command = (state, dispatch) => {
  if (!itemCursor(state)) return false;
  liftListItem(listItem)(state, dispatch);
  return true;
};

/** Enter or Shift+Enter as a new line, or a new item in a list. */
export const newLine = chainCommands(
  openCodeBlock,
  leaveCodeBlockAfterBlanks,
  newlineInCode,
  splitListItem(listItem),
  leaveEmptyItem,
  splitBlock
);

// ── plugins ─────────────────────────────────────────────────────────────────

/** Colours for each code block's code. */
function colours(doc: Node): DecorationSet {
  const decorations: Decoration[] = [];
  doc.forEach((node, offset) => {
    if (node.type !== codeBlock) return;
    let at = offset + 1;
    for (const run of highlight(node.textContent, node.attrs.lang as string)) {
      if (run.colour) decorations.push(Decoration.inline(at, at + run.text.length, { style: `color: ${run.colour}` }));
      at += run.text.length;
    }
  });
  return DecorationSet.create(doc, decorations);
}

const colourKey = new PluginKey<DecorationSet>('colours');
const colouring = new Plugin<DecorationSet>({
  key: colourKey,
  state: {
    init: (_, { doc }) => colours(doc),
    apply: (tr, set) => (tr.docChanged ? colours(tr.doc) : set)
  },
  props: { decorations: (state) => colourKey.getState(state) }
});

/**
 * The zero-width space goes from anything but an empty inline box, and an
 * empty box goes once the cursor is not at its end. Left alone while an
 * input method is composing: changing the text it is working on breaks it.
 *
 * Deleting everything leaves an empty line, not the empty code block or list
 * ProseMirror keeps when the draft began with one.
 */
const tidying = new Plugin({
  appendTransaction(trs, old, state) {
    if (!trs.some((tr) => tr.docChanged || tr.selectionSet) || trs.some((tr) => tr.getMeta('composition'))) return null;
    const all = !old.selection.empty && old.selection.from <= 1 && old.selection.to >= old.doc.content.size - 1;
    const first = state.doc.firstChild!;
    if (all && state.doc.childCount === 1 && ((first.type === codeBlock && !first.content.size) || (isList(first) && !first.textContent))) {
      const tr = state.tr.replaceWith(0, state.doc.content.size, paragraph.create());
      return tr.setSelection(TextSelection.create(tr.doc, 1));
    }
    const { empty, from } = state.selection;
    const cuts: Array<[number, number]> = [];
    state.doc.descendants((node, pos) => {
      const text = node.text;
      if (!text?.includes(ZWSP)) return;
      if (text === ZWSP && code.isInSet(node.marks)) {
        if (!(empty && from === pos + 1)) cuts.push([pos, pos + 1]);
        return;
      }
      for (let i = 0; i < text.length; i++) if (text[i] === ZWSP) cuts.push([pos + i, pos + i + 1]);
    });
    if (!cuts.length) return null;
    const tr = state.tr;
    for (const [a, b] of cuts.reverse()) tr.delete(a, b);
    return tr;
  }
});

export function editorPlugins(): Plugin[] {
  return [
    inputRules({ rules: INPUT_RULES }),
    keymap({
      'Shift-Enter': newLine,
      Enter: newLine,
      ArrowRight: chainCommands(leaveInlineCode, leaveCodeBlockForward),
      ArrowDown: leaveCodeBlockForward,
      ArrowUp: leaveCodeBlockBackward,
      Backspace: chainCommands(undoInputRule, unwrapCodeBlock, liftAtItemStart),
      Tab: chainCommands(indentInCode, nestItem),
      'Shift-Tab': unnestItem,
      'Mod-z': undo,
      'Shift-Mod-z': redo,
      'Mod-y': redo
    }),
    keymap(baseKeymap),
    history(),
    colouring,
    tidying
  ];
}

// ── the view ────────────────────────────────────────────────────────────────

/** Set on a transaction that replaced the draft from outside. */
const EXTERNAL = 'composer-external';

export interface ComposerEditor {
  /** The draft's text, as it is sent. */
  text(): string;
  /** Replaces the whole draft, with the cursor at its end. */
  setText(text: string): void;
  focus(atEnd?: boolean): void;
  /** The cursor is at the very start, with nothing selected. */
  atStart(): boolean;
  /** Enter here would open a code block rather than do anything else. */
  opensBlock(): boolean;
  /** Takes the first `n` characters off the first line; past its end, the
   *  whole line and its break. */
  cutStart(n: number): void;
  /** The first line's text, or null where it holds code or is a code block. */
  firstLine(): string | null;
  /** Whether more than one line or block is there. */
  multiline(): boolean;
  /** Reads `editable` again. */
  refresh(): void;
  destroy(): void;
}

export interface EditorOptions {
  text: string;
  attributes: Record<string, string>;
  editable: () => boolean;
  /** `external`: the change came from `setText`. */
  onChange: (text: string, empty: boolean, external: boolean) => void;
  /** First say on every key, before the editing keys. */
  onKeyDown: (view: EditorView, event: KeyboardEvent) => boolean;
  /** Files pasted with no text: a screenshot, a photo. Without it they are
   *  ignored, as before. Drops are the composer's (Composer.tsx). */
  onFiles?: (files: File[]) => void;
}

/** Pasted text, read as a draft is: fences and inline code come in as code. */
function pasteSlice(text: string): Slice {
  const doc = fromText(text);
  const open = (n: Node | null) => (n?.type === paragraph ? 1 : 0);
  return new Slice(doc.content, open(doc.firstChild), open(doc.lastChild));
}

export function createEditor(mount: HTMLElement, o: EditorOptions): ComposerEditor {
  const view: EditorView = new EditorView(
    { mount },
    {
      state: EditorState.create({ doc: fromText(o.text), plugins: editorPlugins() }),
      attributes: o.attributes,
      editable: () => o.editable(),
      handleKeyDown: (v, event) => o.onKeyDown(v, event),
      // Text, or files that came without any: a web page's formatting has
      // nowhere to go here. In a code block text goes in as it is.
      handlePaste: (v, event) => {
        const files = o.onFiles ? pastedFiles(event.clipboardData) : [];
        if (files.length) {
          o.onFiles!(files);
          return true;
        }
        const text = event.clipboardData?.getData('text/plain');
        if (!text || v.state.selection.$from.parent.type === codeBlock) return false;
        v.dispatch(v.state.tr.replaceSelection(pasteSlice(text)).scrollIntoView());
        return true;
      },
      // What is copied out is what would be sent: fences, backticks and all.
      clipboardTextSerializer: (slice) => {
        try {
          return toText(schema.nodes.doc.create(null, slice.content));
        } catch {
          return slice.content.textBetween(0, slice.content.size, '\n');
        }
      },
      dispatchTransaction(tr) {
        const before = view.state.doc;
        view.updateState(view.state.apply(tr));
        if (view.state.doc !== before) o.onChange(toText(view.state.doc), isEmpty(view.state.doc), !!tr.getMeta(EXTERNAL));
      }
    }
  );

  const firstParagraph = (): Node | null => {
    const first = view.state.doc.firstChild;
    return first?.type === paragraph ? first : null;
  };

  return {
    text: () => toText(view.state.doc),
    setText(text) {
      const doc = fromText(text);
      const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, doc.content);
      tr.setSelection(Selection.atEnd(tr.doc)).setMeta(EXTERNAL, true).setMeta('addToHistory', false);
      view.dispatch(tr);
    },
    focus(atEnd = false) {
      if (atEnd) view.dispatch(view.state.tr.setSelection(Selection.atEnd(view.state.doc)).scrollIntoView());
      view.focus();
    },
    atStart: () => view.state.selection.empty && view.state.selection.from === Selection.atStart(view.state.doc).from,
    opensBlock: () => openCodeBlock(view.state),
    cutStart(n) {
      const first = firstParagraph();
      if (!first || n <= 0) return;
      const tr = n <= first.content.size ? view.state.tr.delete(1, 1 + n) : view.state.tr.delete(0, first.nodeSize);
      view.dispatch(tr);
    },
    firstLine() {
      const first = firstParagraph();
      if (!first) return null;
      let marked = false;
      first.forEach((n) => {
        if (code.isInSet(n.marks)) marked = true;
      });
      return marked ? null : first.textContent.replaceAll(ZWSP, '');
    },
    multiline: () => view.state.doc.childCount > 1,
    refresh: () => view.setProps({}),
    destroy: () => view.destroy()
  };
}
